import _objectSpread from "/Users/fangyu/Code/unifygama/metacrm/metadesk-frontend/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _objectWithoutProperties from "/Users/fangyu/Code/unifygama/metacrm/metadesk-frontend/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
const _excluded = ["account", "batch", "blockNumber", "blockTag", "accessList", "data", "gas", "gasPrice", "maxFeePerGas", "maxPriorityFeePerGas", "nonce", "to", "value"],
  _excluded2 = ["data", "to"];
import { parseAccount } from '../../accounts/utils/parseAccount.js';
import { multicall3Abi } from '../../constants/abis.js';
import { aggregate3Signature } from '../../constants/contract.js';
import { BaseError } from '../../errors/base.js';
import { ChainDoesNotSupportContract, ClientChainNotConfiguredError } from '../../errors/chain.js';
import { RawContractError } from '../../errors/contract.js';
import { decodeFunctionResult } from '../../utils/abi/decodeFunctionResult.js';
import { encodeFunctionData } from '../../utils/abi/encodeFunctionData.js';
import { getChainContractAddress } from '../../utils/chain/getChainContractAddress.js';
import { numberToHex } from '../../utils/encoding/toHex.js';
import { getCallError } from '../../utils/errors/getCallError.js';
import { extract } from '../../utils/formatters/extract.js';
import { formatTransactionRequest } from '../../utils/formatters/transactionRequest.js';
import { createBatchScheduler } from '../../utils/promise/createBatchScheduler.js';
import { assertRequest } from '../../utils/transaction/assertRequest.js';
/**
 * Executes a new message call immediately without submitting a transaction to the network.
 *
 * - Docs: https://viem.sh/docs/actions/public/call.html
 * - JSON-RPC Methods: [`eth_call`](https://ethereum.org/en/developers/docs/apis/json-rpc/#eth_call)
 *
 * @param client - Client to use
 * @param parameters - {@link CallParameters}
 * @returns The call data. {@link CallReturnType}
 *
 * @example
 * import { createPublicClient, http } from 'viem'
 * import { mainnet } from 'viem/chains'
 * import { call } from 'viem/public'
 *
 * const client = createPublicClient({
 *   chain: mainnet,
 *   transport: http(),
 * })
 * const data = await call(client, {
 *   account: '0xf39fd6e51aad88f6f4ce6ab8827279cfffb92266',
 *   data: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
 *   to: '0x70997970c51812dc3a010c7d01b50e0d17dc79c8',
 * })
 */
export async function call(client, args) {
  var _client$batch;
  const {
      account: account_ = client.account,
      batch = Boolean((_client$batch = client.batch) === null || _client$batch === void 0 ? void 0 : _client$batch.multicall),
      blockNumber,
      blockTag = 'latest',
      accessList,
      data,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      to,
      value
    } = args,
    rest = _objectWithoutProperties(args, _excluded);
  const account = account_ ? parseAccount(account_) : undefined;
  try {
    var _client$chain;
    assertRequest(args);
    const blockNumberHex = blockNumber ? numberToHex(blockNumber) : undefined;
    const block = blockNumberHex || blockTag;
    const chainFormat = (_client$chain = client.chain) === null || _client$chain === void 0 || (_client$chain = _client$chain.formatters) === null || _client$chain === void 0 || (_client$chain = _client$chain.transactionRequest) === null || _client$chain === void 0 ? void 0 : _client$chain.format;
    const format = chainFormat || formatTransactionRequest;
    const request = format(_objectSpread(_objectSpread({}, extract(rest, {
      format: chainFormat
    })), {}, {
      from: account === null || account === void 0 ? void 0 : account.address,
      accessList,
      data,
      gas,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      to,
      value
    }));
    if (batch && shouldPerformMulticall({
      request
    })) {
      try {
        return await scheduleMulticall(client, _objectSpread(_objectSpread({}, request), {}, {
          blockNumber,
          blockTag
        }));
      } catch (err) {
        if (!(err instanceof ClientChainNotConfiguredError) && !(err instanceof ChainDoesNotSupportContract)) throw err;
      }
    }
    const response = await client.request({
      method: 'eth_call',
      params: block ? [request, block] : [request]
    });
    if (response === '0x') return {
      data: undefined
    };
    return {
      data: response
    };
  } catch (err) {
    const data = getRevertErrorData(err);
    const {
      offchainLookup,
      offchainLookupSignature
    } = await import('../../utils/ccip.js');
    if ((data === null || data === void 0 ? void 0 : data.slice(0, 10)) === offchainLookupSignature && to) {
      return {
        data: await offchainLookup(client, {
          data,
          to
        })
      };
    }
    throw getCallError(err, _objectSpread(_objectSpread({}, args), {}, {
      account,
      chain: client.chain
    }));
  }
}
// We only want to perform a scheduled multicall if:
// - The request has calldata,
// - The request has a target address,
// - The target address is not already the aggregate3 signature,
// - The request has no other properties (`nonce`, `gas`, etc cannot be sent with a multicall).
function shouldPerformMulticall(_ref) {
  let {
    request
  } = _ref;
  const {
      data,
      to
    } = request,
    request_ = _objectWithoutProperties(request, _excluded2);
  if (!data) return false;
  if (data.startsWith(aggregate3Signature)) return false;
  if (!to) return false;
  if (Object.values(request_).filter(x => typeof x !== 'undefined').length > 0) return false;
  return true;
}
async function scheduleMulticall(client, args) {
  var _client$batch2;
  const {
    batchSize = 1024,
    wait = 0
  } = typeof ((_client$batch2 = client.batch) === null || _client$batch2 === void 0 ? void 0 : _client$batch2.multicall) === 'object' ? client.batch.multicall : {};
  const {
    blockNumber,
    blockTag = 'latest',
    data,
    multicallAddress: multicallAddress_,
    to
  } = args;
  let multicallAddress = multicallAddress_;
  if (!multicallAddress) {
    if (!client.chain) throw new ClientChainNotConfiguredError();
    multicallAddress = getChainContractAddress({
      blockNumber,
      chain: client.chain,
      contract: 'multicall3'
    });
  }
  const blockNumberHex = blockNumber ? numberToHex(blockNumber) : undefined;
  const block = blockNumberHex || blockTag;
  const {
    schedule
  } = createBatchScheduler({
    id: "".concat(client.uid, ".").concat(block),
    wait,
    shouldSplitBatch(args) {
      const size = args.reduce((size, _ref2) => {
        let {
          data
        } = _ref2;
        return size + (data.length - 2);
      }, 0);
      return size > batchSize * 2;
    },
    fn: async requests => {
      const calls = requests.map(request => ({
        allowFailure: true,
        callData: request.data,
        target: request.to
      }));
      const calldata = encodeFunctionData({
        abi: multicall3Abi,
        args: [calls],
        functionName: 'aggregate3'
      });
      const data = await client.request({
        method: 'eth_call',
        params: [{
          data: calldata,
          to: multicallAddress
        }, block]
      });
      return decodeFunctionResult({
        abi: multicall3Abi,
        args: [calls],
        functionName: 'aggregate3',
        data: data || '0x'
      });
    }
  });
  const [{
    returnData,
    success
  }] = await schedule({
    data,
    to
  });
  if (!success) throw new RawContractError({
    data: returnData
  });
  if (returnData === '0x') return {
    data: undefined
  };
  return {
    data: returnData
  };
}
export function getRevertErrorData(err) {
  if (!(err instanceof BaseError)) return undefined;
  const error = err.walk();
  return typeof error.data === 'object' ? error.data.data : error.data;
}
//# sourceMappingURL=call.js.map